/**
 * Per-section Action History — wire types + the pure tree builder.
 *
 * Shared by the Word add-in (`ActionHistoryTab`) and the web app
 * (`ActionHistoryScreen`) so both render the same hierarchy from the same
 * `GET /runs/{id}/sections/{mid}/action-history` payload:
 *
 *   Superintendent → Lead planner → Layer n → node «title» → tool calls
 *   Drafting agent → tool calls → reasoning → draft
 *   Resolve issues pass · requested by X → tool calls
 *   Edit approved by X / Manual save by X / Proposal accepted by X …
 *
 * Pure and dependency-free: node-testable, no React, no fetch.
 */

/** One structured literature result from a `search_literature` call. */
export interface WebResult {
  source: string // "pubmed" | "clinical_trials" | "biorxiv" | "chembl"
  title: string
  year: number | null
  journal: string | null
  url: string | null
  pmid: string | null
  nct_id: string | null
  doi: string | null
  abstract: string
  /** Key the drafter cited it by ([CITE:key] → EndNote placeholder). */
  citation_key?: string | null
  authors?: string[] | null
  study_type?: string | null
  primary_endpoints?: string | null
  has_results?: boolean | null
  results_summary?: string | null
}

/** Fields common to every context-preparation tool call. */
export interface ActionCallBase {
  tool_name: string
  transaction_id: string | null
  input_params: Record<string, unknown>
  outcome: 'success' | 'failure' | 'written_off' | 'partial'
  result_length: number
  duration_ms: number | null
  started_at: string | null
  // Agent-span identity for the hierarchy. Null on flat / legacy runs.
  span_id?: string | null
  parent_span_id?: string | null
  node_id?: string | null
  agent_role?: string | null // superintendent | lead | child | main | retrieval | resolve
  // Human-readable label of the sub-agent that made this call (e.g. a resolve
  // child's target issue marker). Labels the agent node it hangs under.
  span_title?: string | null
  // "Agent i/N" position of the superintendent node that made this call.
  node_index?: number | null
  node_total?: number | null
  // submit_draft only: attempt number, outcome (accepted | staged | rerouted |
  // rejected | pending) and declared/received/missing sections.
  submit_progress?: SubmitProgress | null
  /** Set when the call failed. */
  error?: string | null
  /** The first ~2,000 characters of what the tool returned. Optional: absent
   *  until the server sends it (see the Action History server spec); the UI
   *  shows it only when present. */
  result_preview?: string | null
  /** calculate / create_artefact: "How this was calculated" — the same shape
   *  the citation popover receives. Null for every other tool. */
  derivation?: ActionDerivation | null
}

/** A source an input of a computed number was read from. */
export interface ActionCitation {
  citation_number: number
  document_name: string
  section_name: string
  page_no: number
  context: string
  transaction_id: string
}

/** One input of a computed number. */
export interface ActionDerivationInput {
  name: string
  value?: unknown
  /** source | design | unsourced (calculate); ref | artefact | note | literal (create_artefact). */
  kind: string
  source: string
  verified: boolean
  reason: string
  citation: ActionCitation | null
}

/** One step of a derivation trace; ``{name}`` in ``text`` names an input. */
export interface ActionDerivationStep {
  kind: string
  text: string
  input_refs?: string[]
  citation_numbers?: Record<string, number>
}

/** "How this was calculated" for a calculate / create_artefact call. */
export interface ActionDerivation {
  transaction_id: string
  kind: string
  operation: string
  method: string
  value: string
  unit: string
  rationale: string
  agent_note?: string
  matched_values: string[]
  verified: boolean
  trace: ActionDerivationStep[]
  inputs: ActionDerivationInput[]
}

export interface SubmitProgress {
  attempt?: number
  outcome?: string
  declared?: number
  received?: number
  missing?: string[]
}

const SUBMIT_OUTCOME_LABEL: Record<string, string> = {
  accepted: 'accepted',
  staged: 'partial, staged',
  rerouted: 'rerouted to another agent',
  rejected: 'rejected',
  pending: 'not accepted yet',
}

/** "Submission #2 · partial, staged · 3/7 sections" — tells repeated submits apart. */
export function submitProgressLabel(p: SubmitProgress): { label: string; sub?: string } {
  const outcome = p.outcome ?? ''
  const label = `Submission${p.attempt ? ` #${p.attempt}` : ''} · ${SUBMIT_OUTCOME_LABEL[outcome] ?? (outcome || 'submitted')}`
  if (typeof p.declared !== 'number' || p.declared <= 0) return { label }
  const missing = Array.isArray(p.missing) ? p.missing : []
  const pending = missing.length > 0 && outcome !== 'accepted' ? ` · pending ${missing.join(', ')}` : ''
  return { label, sub: `${p.received ?? 0}/${p.declared} sections${pending}` }
}

export interface InternetCall extends ActionCallBase {
  query: string
  web_total: number | null
  web_sources: string[]
  web_results: WebResult[]
  goal?: string
  web_summary?: string
  web_sub_queries?: string[]
  web_used_refs?: Array<Record<string, unknown>>
}

export interface LibraryCall extends ActionCallBase {
  cascade_pass: number | null
  source_detail: {
    source_ref: Record<string, unknown>
    section_number: string | null
    snippet: string
    content_length: number
  } | null
}

/** One node of the superintendent plan skeleton (empty for the flat path). */
export interface PlanNode {
  node_id: string
  target_section_id: string
  title: string
  depends_on: string[]
  layer: number
}

/** One agent / sub-agent span seen on the section (server-synthesized parents included). */
export interface AgentSpanNode {
  span_id: string
  parent_span_id: string | null
  role: string
  node_id: string | null
  title: string | null
  target_section_id: string | null
}

/** An attributable act against the section — approvals, saves, resolve passes, drafting authority. */
export interface ActivityEvent {
  kind: 'activity'
  started_at: string | null
  action: string
  actor: string
  actor_id: string | null
  actor_type: string // human | service | ai_agent | import | inspector
  authority_actor_id: string | null
  outcome: string // applied | refused | conflict | skipped | error
  section_version: number | null
  detail: Record<string, unknown>
  /** The span root the act's tool calls hang off (a resolve pass, an approved rewrite). */
  span_id: string | null
}

/** A non-retrieval tool call — terminal submit_* or any other tool. */
export interface OtherCall extends ActionCallBase {
  phase?: string | null
}

export type RetrievalEvent = LibraryCall & { kind: 'retrieval'; count: number; total_duration_ms: number | null }
export type InternetEvent = InternetCall & { kind: 'internet'; count: number; total_duration_ms: number | null }
export type GenericToolEvent = OtherCall & { kind: 'tool'; count: number; total_duration_ms: number | null }
export type ReasoningEvent = {
  kind: 'reasoning'
  started_at: string | null
  thinking_tokens: number
  traces: string[]
  traces_available: boolean
}
export type DraftEvent = {
  kind: 'draft'
  started_at: string | null
  completed_at: string | null
  duration_ms: number | null
  model: string
  input_tokens: number
  output_tokens: number
  thinking_tokens: number
  draft_length: number
}

export type TimelineEvent =
  | RetrievalEvent
  | InternetEvent
  | GenericToolEvent
  | ReasoningEvent
  | DraftEvent
  | ActivityEvent

/** One selectable attempt of a section's agent log (archived regens + the live one). */
export interface AttemptInfo {
  attempt_number: number
  archived_at: string | null
  final_spine_status: string | null
  guidance: string | null
  is_current: boolean
}

export interface ActionHistoryPayload {
  module_id: string
  available: boolean
  internet_calls: InternetCall[]
  library_calls: LibraryCall[]
  other_calls?: OtherCall[]
  timeline?: TimelineEvent[]
  plan_nodes?: PlanNode[]
  plan_synthesized?: boolean
  has_hierarchy?: boolean
  agents?: AgentSpanNode[]
  attempts?: AttemptInfo[]
  attempt?: number
}

// ---------------------------------------------------------------------------
// Tree
// ---------------------------------------------------------------------------

export type ActionTreeKind = 'agent' | 'layer' | 'activity' | 'leaf' | 'note'

export interface ActionTreeNode {
  key: string
  kind: ActionTreeKind
  icon: string
  label: string
  sub?: string
  /** ISO time used for ordering (earliest descendant for branches). */
  at: string | null
  children: ActionTreeNode[]
  /** The timeline event a leaf (or an activity node) renders. */
  event?: TimelineEvent
  /** Agent role for `agent` nodes; outcome / actor type for `activity` nodes. */
  role?: string
  outcome?: string
  actorType?: string
}

const ROLE_ICON: Record<string, string> = {
  superintendent: '🧭',
  lead: '🧠',
  child: '📄',
  main: '✍️',
  resolve: '🩹',
  retrieval: '📚',
  cascade: '📚',
}

const KIND_ICON: Record<TimelineEvent['kind'], string> = {
  retrieval: '📚',
  internet: '🌐',
  tool: '🔧',
  reasoning: '🧠',
  draft: '✍️',
  activity: '👤',
}

// Terminal / control tools get a distinct glyph from generic tools so the
// submitted draft/rewrite reads as the agent's output, not another fetch.
const TOOL_ICON: Record<string, string> = {
  submit_draft: '📝',
  submit_rewrite: '📝',
  submit_plan: '🗂️',
}

/** Friendly label for a terminal/other tool leaf. */
export function toolLeafLabel(toolName: string): string {
  switch (toolName) {
    case 'submit_draft':
      return 'Submitted draft'
    case 'submit_rewrite':
      return 'Submitted rewrite'
    case 'submit_plan':
      return 'Submitted plan'
    case 'submit_retrieval':
      return 'Returned retrieval results'
    case 'list_documents':
      return 'Listed documents'
    case 'get_document_inventory':
      return 'Read document inventory'
    default:
      return toolName
  }
}

// Fallback labels for agent nodes with no span title, by role.
const AGENT_ROLE_LABEL: Record<string, string> = {
  superintendent: 'Superintendent',
  lead: 'Lead planner',
  child: 'Sub-agent',
  main: 'Agent',
  resolve: 'Resolve pass',
  retrieval: 'Retrieval sub-agent',
  cascade: 'Retrieval sub-agent',
}

const ACTOR_ICON: Record<string, string> = {
  human: '👤',
  ai_agent: '🤖',
  service: '⚙️',
  import: '📥',
  inspector: '🔍',
}

function str(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null
}
function clip(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n)}…` : s
}
function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null
}

/** Human label (+ optional sub-line) for one activity event. */
export function activityLabel(ev: ActivityEvent): { label: string; sub?: string } {
  const d = ev.detail ?? {}
  const actor = ev.actor || 'unknown'
  const version = ev.section_version != null ? `v${ev.section_version}` : undefined
  switch (ev.action) {
    case 'section.resolve_issues': {
      const requestedBy = str(d.requested_by) ?? actor
      const filled = num(d.filled)
      const remaining = num(d.remaining)
      const counts =
        filled != null && remaining != null ? ` · ${filled} filled, ${remaining} left` : ''
      if (ev.outcome === 'skipped') {
        const mode = d.mode === 'pause' ? 'paused' : 'stopped'
        return { label: `Resolve issues pass ${mode} · requested by ${requestedBy}`, sub: version }
      }
      if (ev.outcome === 'conflict') {
        return { label: `Resolve issues pass skipped · section changed underneath · requested by ${requestedBy}` }
      }
      if (ev.outcome === 'error') {
        return { label: `Resolve issues pass failed · requested by ${requestedBy}` }
      }
      return { label: `Resolve issues pass · requested by ${requestedBy}${counts}`, sub: version }
    }
    case 'section.edit_applied': {
      const kind = str(d.edit_kind) ?? (d.source === 'agent_redraft' ? 'approved_proposal' : 'manual_save')
      const verb =
        kind === 'approved_proposal'
          ? "Approved Asthra's rewrite"
          : kind === 'bulk_save'
            ? 'Bulk save'
            : 'Manual save'
      // The chat edit-highlight trigger — a short label; the user's instruction
      // (and the text they highlighted) heads the edit's subtree as a note child
      // (see buildActionTree), rather than being crammed into this row.
      const instruction = str(d.instruction)
      if (instruction) return { label: 'Edit' }
      // The actor is already shown as a chip on the row, so no `· ${actor}` here.
      return { label: verb, sub: version }
    }
    case 'section.adhoc_decision': {
      const decision = str(d.decision) ?? str(d.status) ?? str(d.outcome) ?? 'decided'
      return { label: `Proposal ${decision} · ${actor}`, sub: version }
    }
    case 'section.transition': {
      const to = str(d.to) ?? str(d.to_status) ?? str(d.status)
      return { label: to ? `Marked ${to} · ${actor}` : `Section transition · ${actor}`, sub: version }
    }
    case 'section.agent_draft_authority':
      // The actor ("Asthra") is already the row's chip, so just "Drafted".
      return {
        label: 'Drafted',
        sub: ev.authority_actor_id ? `authority: ${ev.authority_actor_id}` : undefined,
      }
    case 'section.audit_annotate':
      return { label: `Regulatory audit annotated · ${actor}`, sub: version }
    case 'section.regen_reset': {
      const guidance = str(d.guidance)
      const requestedBy = str(d.requested_by) ?? actor
      if (guidance) return { label: `Regenerated · ${requestedBy}`, sub: `“${clip(guidance, 80)}”` }
      return { label: `Regenerated · ${requestedBy}`, sub: version }
    }
    case 'section.edit_legacy':
      return { label: `${actor} edit`, sub: version }
    default:
      return { label: `${ev.action} · ${actor}`, sub: version }
  }
}

type ToolEvent = RetrievalEvent | InternetEvent | GenericToolEvent

function isTool(ev: TimelineEvent): ev is ToolEvent {
  return ev.kind === 'retrieval' || ev.kind === 'internet' || ev.kind === 'tool'
}

function byAt(a: ActionTreeNode, b: ActionTreeNode): number {
  if (a.at === b.at) return 0
  if (a.at === null) return 1
  if (b.at === null) return -1
  return a.at < b.at ? -1 : 1
}

function earliest(nodes: ActionTreeNode[]): string | null {
  let best: string | null = null
  for (const n of nodes) {
    if (n.at !== null && (best === null || n.at < best)) best = n.at
  }
  return best
}

function leaf(ev: TimelineEvent, key: string): ActionTreeNode {
  let label: string
  let icon = KIND_ICON[ev.kind]
  if (ev.kind === 'reasoning') label = 'Reasoning'
  else if (ev.kind === 'draft') label = 'Drafted section'
  else if (ev.kind === 'activity') label = activityLabel(ev).label
  else if (ev.kind === 'tool') {
    label = toolLeafLabel(ev.tool_name)
    icon = TOOL_ICON[ev.tool_name] ?? KIND_ICON.tool
    if (ev.submit_progress) {
      const sp = submitProgressLabel(ev.submit_progress)
      if (ev.submit_progress.outcome === 'rejected') icon = '⚠️'
      return { key, kind: 'leaf', icon, label: sp.label, sub: sp.sub, at: ev.started_at, children: [], event: ev }
    }
  } else label = ev.tool_name
  return { key, kind: 'leaf', icon, label, at: ev.started_at, children: [], event: ev }
}

function agentNode(key: string, role: string, label: string, children: ActionTreeNode[], sub?: string): ActionTreeNode {
  children.sort(byAt)
  return {
    key,
    kind: 'agent',
    icon: ROLE_ICON[role] ?? '•',
    label,
    sub,
    at: earliest(children),
    children,
    role,
  }
}

function activityNode(ev: ActivityEvent, key: string, children: ActionTreeNode[]): ActionTreeNode {
  const { label, sub } = activityLabel(ev)
  children.sort(byAt)
  return {
    key,
    kind: 'activity',
    icon: ACTOR_ICON[ev.actor_type] ?? '👤',
    label,
    sub,
    at: ev.started_at,
    children,
    event: ev,
    outcome: ev.outcome,
    actorType: ev.actor_type,
  }
}

function callsLabel(n: number): string {
  return `${n} call${n === 1 ? '' : 's'}`
}

/**
 * Fold the calls a retrieval sub-agent made (role `retrieval`) into one nested
 * agent node per sub-agent span, labeled by its span title (the requirement it
 * served), so an agent's own calls and each retrieve_from_library call's inner
 * searches / section + table fetches read as a hierarchy instead of one flat list.
 */
function nestRetrievalSpans(leaves: ActionTreeNode[]): ActionTreeNode[] {
  const out: ActionTreeNode[] = []
  const bySpan = new Map<string, ActionTreeNode[]>()
  const titles = new Map<string, string>()
  for (const n of leaves) {
    const ev = n.event
    if (ev && isTool(ev) && ev.agent_role === 'retrieval' && ev.span_id) {
      const arr = bySpan.get(ev.span_id) ?? []
      arr.push(n)
      bySpan.set(ev.span_id, arr)
      if (ev.span_title && !titles.has(ev.span_id)) titles.set(ev.span_id, ev.span_title)
      continue
    }
    out.push(n)
  }
  for (const [spanId, calls] of bySpan) {
    const label = titles.get(spanId) ?? AGENT_ROLE_LABEL.retrieval
    out.push(agentNode(`span:${spanId}`, 'retrieval', label, calls, `Retrieval sub-agent · ${callsLabel(calls.length)}`))
  }
  return out.sort(byAt)
}

/**
 * Build the hierarchy from a payload. Deterministic: the same payload always
 * yields the same keys, so open/closed state keyed on them survives a refetch.
 */
export function buildActionTree(payload: ActionHistoryPayload): ActionTreeNode[] {
  const timeline = payload.timeline ?? []
  const agents = new Map<string, AgentSpanNode>()
  for (const a of payload.agents ?? []) agents.set(a.span_id, a)
  // Spans seen on tool events but missing from the index (older servers).
  for (const ev of timeline) {
    if (isTool(ev) && ev.span_id && !agents.has(ev.span_id)) {
      agents.set(ev.span_id, {
        span_id: ev.span_id,
        parent_span_id: ev.parent_span_id ?? null,
        role: ev.agent_role ?? 'main',
        node_id: ev.node_id ?? null,
        title: ev.span_title ?? null,
        target_section_id: null,
      })
    }
  }

  const rootOf = (spanId: string): string => {
    let cur = spanId
    const guard = new Set<string>()
    for (;;) {
      const a = agents.get(cur)
      if (!a || !a.parent_span_id || guard.has(cur)) return cur
      guard.add(cur)
      cur = a.parent_span_id
    }
  }
  const chainHas = (spanId: string, pred: (a: AgentSpanNode) => boolean): boolean => {
    let cur: string | null = spanId
    const guard = new Set<string>()
    while (cur && !guard.has(cur)) {
      guard.add(cur)
      const a = agents.get(cur)
      if (!a) return false
      if (pred(a)) return true
      cur = a.parent_span_id
    }
    return false
  }

  const activities = timeline.filter((e): e is ActivityEvent => e.kind === 'activity')
  const activityBySpan = new Map<string, ActivityEvent>()
  for (const a of activities) if (a.span_id) activityBySpan.set(a.span_id, a)

  // Bucket tool events.
  // Leaves adopted by an activity root, keyed by their EXACT span so the pass's
  // sub-agents can be rebuilt as nested nodes (each labeled by its span title —
  // e.g. a resolve child's target marker), not flattened onto the pass.
  const activityLeavesBySpan = new Map<string, ActionTreeNode[]>() // exact span_id → leaves
  const superLead: ActionTreeNode[] = []
  const superByNode = new Map<string, ActionTreeNode[]>()
  const superOther: ActionTreeNode[] = []
  const mainLeaves: ActionTreeNode[] = []
  const orphans: ActionTreeNode[] = []
  let sawSuper = false

  timeline.forEach((ev, i) => {
    if (!isTool(ev)) return
    const node = leaf(ev, `tl-${i}`)
    const spanId = ev.span_id ?? null
    if (!spanId) {
      if (ev.node_id) {
        sawSuper = true
        const arr = superByNode.get(ev.node_id) ?? []
        arr.push(node)
        superByNode.set(ev.node_id, arr)
      } else if (ev.agent_role === 'lead') {
        sawSuper = true
        superLead.push(node)
      } else {
        orphans.push(node)
      }
      return
    }
    const root = rootOf(spanId)
    const act = activityBySpan.get(root) ?? (chainHas(spanId, (a) => activityBySpan.has(a.span_id)) ? undefined : undefined)
    // Any span in the chain that is an activity root adopts the leaf.
    let adopted: string | null = null
    let cur: string | null = spanId
    const guard = new Set<string>()
    while (cur && !guard.has(cur)) {
      guard.add(cur)
      if (activityBySpan.has(cur)) {
        adopted = cur
        break
      }
      cur = agents.get(cur)?.parent_span_id ?? null
    }
    void act
    if (adopted) {
      const arr = activityLeavesBySpan.get(spanId) ?? []
      arr.push(node)
      activityLeavesBySpan.set(spanId, arr)
      return
    }
    const rootAgent = agents.get(root)
    const isSuper =
      (rootAgent?.role === 'superintendent') ||
      chainHas(spanId, (a) => a.role === 'lead' || a.role === 'child' || a.role === 'superintendent') ||
      // A child node's retrieval sub-agent inherits its node_id — keep it under
      // that node even when the child's own span is missing from the index.
      (ev.agent_role === 'retrieval' && Boolean(ev.node_id))
    if (isSuper) {
      sawSuper = true
      const selfAgent = agents.get(spanId)
      if (chainHas(spanId, (a) => a.role === 'lead') && !ev.node_id) {
        superLead.push(node)
      } else {
        const nodeId = ev.node_id ?? selfAgent?.node_id ?? null
        if (nodeId) {
          const arr = superByNode.get(nodeId) ?? []
          arr.push(node)
          superByNode.set(nodeId, arr)
        } else {
          superOther.push(node)
        }
      }
      return
    }
    mainLeaves.push(node)
  })

  const roots: ActionTreeNode[] = []

  // Superintendent tree.
  if (sawSuper || (payload.plan_nodes?.length ?? 0) > 0) {
    const planNodes = payload.plan_nodes ?? []
    const layers = new Map<number, PlanNode[]>()
    for (const n of planNodes) {
      const arr = layers.get(n.layer) ?? []
      arr.push(n)
      layers.set(n.layer, arr)
    }
    const known = new Set(planNodes.map((n) => n.node_id))
    // "Agent i/N" — prefer the server's number stamped on the node's calls (it
    // numbers in execution order); fall back to plan order for nodes with none.
    const orderedPlan = Array.from(layers.keys())
      .sort((a, b) => a - b)
      .flatMap((lk) => layers.get(lk) ?? [])
    const agentTitle = (nodeId: string, title: string): string => {
      const stamped = (superByNode.get(nodeId) ?? [])
        .map((c) => c.event as ActionCallBase | undefined)
        .find((e) => typeof e?.node_index === 'number')
      const pos = orderedPlan.findIndex((n) => n.node_id === nodeId)
      const i = stamped?.node_index ?? (pos >= 0 ? pos + 1 : null)
      const n = stamped?.node_total ?? (orderedPlan.length || null)
      return i && n && n > 1 ? `Agent ${i}/${n} · ${title}` : title
    }
    const children: ActionTreeNode[] = []
    children.push(agentNode('lead', 'lead', 'Lead planner', nestRetrievalSpans(superLead), callsLabel(superLead.length)))
    for (const lk of Array.from(layers.keys()).sort((a, b) => a - b)) {
      const layerChildren = (layers.get(lk) ?? []).map((n) => {
        const calls = superByNode.get(n.node_id) ?? []
        return agentNode(
          `node:${n.node_id}`,
          'child',
          agentTitle(n.node_id, n.title || n.node_id),
          nestRetrievalSpans(calls),
          `→ §${n.target_section_id} · ${callsLabel(calls.length)}`,
        )
      })
      children.push({
        key: `layer:${lk}`,
        kind: 'layer',
        icon: '',
        label: `Layer ${lk}`,
        at: earliest(layerChildren),
        children: layerChildren,
      })
    }
    // Nodes seen on calls but absent from the plan skeleton.
    for (const [nodeId, calls] of superByNode) {
      if (known.has(nodeId)) continue
      children.push(agentNode(`node:${nodeId}`, 'child', agentTitle(nodeId, nodeId), nestRetrievalSpans(calls), callsLabel(calls.length)))
    }
    if (superOther.length > 0) children.push(agentNode('super-other', 'main', 'Other calls', nestRetrievalSpans(superOther)))
    const total = superLead.length + superOther.length + Array.from(superByNode.values()).reduce((s, a) => s + a.length, 0)
    const sub = `${planNodes.length} node${planNodes.length === 1 ? '' : 's'} · ${layers.size} layer${layers.size === 1 ? '' : 's'} · ${callsLabel(total)}${payload.plan_synthesized ? ' · synthesized plan' : ''}`
    const superNode = agentNode('super', 'superintendent', 'Superintendent', children, sub)
    // Layers keep plan order, not time order.
    superNode.children = children
    roots.push(superNode)
  }

  // Drafting agent (flat path) — its tool calls, then reasoning, then draft.
  const draftingChildren = nestRetrievalSpans(mainLeaves)
  timeline.forEach((ev, i) => {
    if (ev.kind === 'reasoning' || ev.kind === 'draft') draftingChildren.push(leaf(ev, `tl-${i}`))
  })
  if (draftingChildren.length > 0) {
    // Reasoning/draft without timestamps must still trail the tool calls.
    const timed = draftingChildren.filter((c) => c.at !== null).sort(byAt)
    const untimed = draftingChildren.filter((c) => c.at === null)
    const node = agentNode('main', 'main', 'Drafting agent', [], callsLabel(mainLeaves.length))
    node.children = [...timed, ...untimed]
    node.at = earliest(node.children)
    // A drafting root that only holds reasoning/draft on a superintendent run
    // is that run's own tail — keep it under the superintendent instead.
    if (mainLeaves.length === 0 && roots.length > 0 && roots[0].role === 'superintendent') {
      roots[0].children.push(...node.children)
    } else {
      roots.push(node)
    }
  }

  // Activities (chronological). Each adopts its work as a NESTED sub-agent tree:
  // the pass root's direct child spans become their own agent nodes (labeled by
  // span title — a resolve child's target marker), each carrying its own tool
  // calls and any deeper retrieval sub-agents. Leaves recorded directly on the
  // pass root attach to it.
  const childSpansOf = (spanId: string): AgentSpanNode[] => {
    const out: AgentSpanNode[] = []
    for (const a of agents.values()) if (a.parent_span_id === spanId) out.push(a)
    return out
  }
  const subtreeForSpan = (spanId: string, guard: Set<string>): ActionTreeNode => {
    const a = agents.get(spanId)
    const own = activityLeavesBySpan.get(spanId) ?? []
    const kids: ActionTreeNode[] = []
    if (!guard.has(spanId)) {
      guard.add(spanId)
      for (const c of childSpansOf(spanId)) kids.push(subtreeForSpan(c.span_id, guard))
    }
    const role = a?.role ?? 'main'
    const label = a?.title || AGENT_ROLE_LABEL[role] || 'Sub-agent'
    const n = agentNode(`span:${spanId}`, role, label, [...own, ...kids])
    n.sub = callsLabel(countLeaves(n))
    return n
  }
  const activityChildren = (rootSpanId: string): ActionTreeNode[] => {
    const own = activityLeavesBySpan.get(rootSpanId) ?? []
    const guard = new Set<string>([rootSpanId])
    const kids = childSpansOf(rootSpanId).map((c) => subtreeForSpan(c.span_id, guard))
    return [...own, ...kids]
  }
  activities.forEach((ev, i) => {
    const key = `act-${ev.span_id ?? i}-${ev.started_at ?? i}`
    const children = ev.span_id ? activityChildren(ev.span_id) : []
    const n = activityNode(ev, key, children)
    // A chat edit-highlight carries the user's instruction (and the text they
    // highlighted). Lead the edit's subtree with it as a note, so the row label
    // stays short and the full input reads inside on expand. Unshift, because
    // activityNode has already time-sorted the children and the input must lead.
    const instruction = ev.action === 'section.edit_applied' ? str(ev.detail?.instruction) : null
    if (instruction) {
      const selected = str(ev.detail?.selected_text)
      n.children.unshift({
        key: `${key}-input`,
        kind: 'note',
        icon: '',
        label: instruction,
        sub: selected ? `on “${clip(selected, 200)}”` : undefined,
        at: ev.started_at,
        children: [],
      })
    }
    roots.push(n)
  })

  if (orphans.length > 0) roots.push(agentNode('orphan', 'retrieval', 'Other calls', orphans, callsLabel(orphans.length)))

  return roots.sort(byAt)
}

/** Flat count of tool-call leaves under a node (for badges). */
export function countLeaves(node: ActionTreeNode): number {
  if (node.kind === 'leaf') return node.event && isTool(node.event) ? 1 : 0
  return node.children.reduce((s, c) => s + countLeaves(c), 0)
}

// ---------------------------------------------------------------------------
// Tool types, search and filter
// ---------------------------------------------------------------------------

/** What kind of work a tool call did — the filter chips in the Actions tab. */
export type ToolCategory = 'calculation' | 'table' | 'library' | 'search' | 'guidance' | 'notes' | 'drafting' | 'other'

export const TOOL_CATEGORY_LABEL: Record<ToolCategory, string> = {
  calculation: 'Calculations',
  table: 'Tables',
  library: 'Library reads',
  search: 'Literature & web',
  guidance: 'Guidance',
  notes: 'Notes',
  drafting: 'Drafting',
  other: 'Other',
}

/** Chip order: the analysis tools first — they are what a reviewer hunts for. */
export const TOOL_CATEGORY_ORDER: ToolCategory[] = [
  'calculation',
  'table',
  'library',
  'search',
  'guidance',
  'notes',
  'drafting',
  'other',
]

/** The category of a tool, by name. Unknown tools fall to ``other``. */
export function toolCategory(toolName: string): ToolCategory {
  const n = toolName.toLowerCase()
  if (n === 'calculate') return 'calculation'
  if (n === 'create_artefact' || n.includes('artefact')) return 'table'
  if (n.includes('guidance') || n.includes('regulatory')) return 'guidance'
  if (n.includes('literature') || n.includes('web') || n.includes('internet') || n.includes('pubmed') || n.includes('trial'))
    return 'search'
  if (n.includes('note')) return 'notes'
  if (n.startsWith('submit_') || n.includes('draft_part') || n.startsWith('append_') || n.includes('checklist'))
    return 'drafting'
  if (
    n.startsWith('read_') ||
    n.startsWith('find_') ||
    n.startsWith('get_') ||
    n.startsWith('list_') ||
    n.includes('search') ||
    n.includes('view_document') ||
    n.includes('section')
  )
    return 'library'
  return 'other'
}

/** The category of a leaf's event, or null when it is not a tool call
 *  (reasoning, the draft itself, a writer's activity). */
export function eventCategory(ev: TimelineEvent | undefined): ToolCategory | null {
  if (!ev) return null
  if (ev.kind === 'retrieval') return toolCategory(ev.tool_name) === 'guidance' ? 'guidance' : 'library'
  if (ev.kind === 'internet') return 'search'
  if (ev.kind === 'tool') return toolCategory(ev.tool_name)
  return null
}

/** Everything a search box should match on a leaf: its label, tool name,
 *  parameters, result preview, error and derivation. Lower-cased. */
export function leafSearchText(node: ActionTreeNode): string {
  const parts: string[] = [node.label, node.sub ?? '']
  const ev = node.event
  if (ev && isTool(ev)) {
    parts.push(ev.tool_name, JSON.stringify(ev.input_params ?? {}))
    if (ev.result_preview) parts.push(ev.result_preview)
    if (ev.error) parts.push(ev.error)
    const d = ev.derivation
    if (d) parts.push(d.operation, d.method, d.value, d.unit, ...d.trace.map((s) => s.text), ...d.inputs.map((i) => `${i.name} ${String(i.value ?? '')} ${i.source}`))
  }
  return parts.join(' ').toLowerCase()
}

export interface ActionFilter {
  /** Only these categories; empty or null = all. */
  categories?: ReadonlySet<ToolCategory> | null
  /** Free text; every whitespace-separated term must match. */
  query?: string
}

/** True when nothing would be filtered out. */
export function isEmptyFilter(f: ActionFilter): boolean {
  return (!f.categories || f.categories.size === 0) && !(f.query ?? '').trim()
}

function leafMatches(node: ActionTreeNode, f: ActionFilter, terms: string[]): boolean {
  const cats = f.categories
  if (cats && cats.size > 0) {
    const c = eventCategory(node.event)
    if (!c || !cats.has(c)) return false
  }
  if (terms.length === 0) return true
  const hay = leafSearchText(node)
  return terms.every((t) => hay.includes(t))
}

/** The tree with only matching leaves, keeping the agents/layers above them
 *  (branches left empty are dropped). Returns the input when the filter is empty. */
export function filterActionTree(nodes: ActionTreeNode[], f: ActionFilter): ActionTreeNode[] {
  if (isEmptyFilter(f)) return nodes
  const terms = (f.query ?? '').toLowerCase().split(/\s+/).filter(Boolean)
  const walk = (list: ActionTreeNode[]): ActionTreeNode[] => {
    const out: ActionTreeNode[] = []
    for (const n of list) {
      if (n.kind === 'leaf') {
        if (leafMatches(n, f, terms)) out.push(n)
        continue
      }
      if (n.kind === 'note') continue
      const kids = walk(n.children)
      if (kids.length > 0) out.push({ ...n, children: kids })
      else if (n.kind === 'activity' && n.event && n.children.length === 0 && leafMatches(n, f, terms)) out.push(n)
    }
    return out
  }
  return walk(nodes)
}

/** Tool-call leaves per category across the whole tree — the chip counts. */
export function countByCategory(nodes: ActionTreeNode[]): Record<ToolCategory, number> {
  const out = Object.fromEntries(TOOL_CATEGORY_ORDER.map((c) => [c, 0])) as Record<ToolCategory, number>
  const walk = (list: ActionTreeNode[]) => {
    for (const n of list) {
      if (n.kind === 'leaf') {
        const c = eventCategory(n.event)
        if (c) out[c] += 1
      } else walk(n.children)
    }
  }
  walk(nodes)
  return out
}
